/** @jsx jsx */
import { css, jsx } from "@emotion/core"
import { useEffect, useMemo, useRef } from "react"
import { IShakaPlayer, onPlayerTimeUpdate } from "./utils/shaka"

interface ICaptionProps {
  availableCaptions: { src: string; lang: string; id: string }[]
  currentCaptionId?: string
  player: IShakaPlayer | null
}

export function Captions({ player, availableCaptions = [], currentCaptionId }: ICaptionProps) {
  const captionRef = useRef<HTMLDivElement>(null)

  const vttManager = useMemo(() => new VttManager(), [])

  useEffect(() => {
    vttManager.setAvailableCaptions(availableCaptions)
  }, [vttManager, JSON.stringify(availableCaptions)])

  useEffect(() => {
    vttManager.setCurrentCaptionId(currentCaptionId ?? null)
  }, [vttManager, currentCaptionId])

  useEffect(
    () =>
      onPlayerTimeUpdate(player, e => {
        vttManager.setCurrentTime(e.target.currentTime ?? 0)
      }),
    [player, vttManager],
  )

  useEffect(() => {
    vttManager.onTextChange(text => {
      if (!captionRef.current) {
        return
      }

      if (text) {
        captionRef.current.style.visibility = "visible"
        captionRef.current.innerHTML = text.split("\n").join("<br/>")
      } else {
        captionRef.current.style.visibility = "hidden"
      }
    })
  }, [])

  const currentCaption = availableCaptions.find(caption => caption.id === currentCaptionId)

  if (!currentCaptionId || !currentCaption) {
    return null
  }

  return (
    <div
      css={css`
        position: absolute;
        top: 0;
        left: 0;
        bottom: 54px;
        right: 0;
        display: flex;
        align-items: flex-end;
        justify-content: center;
        z-index: 1000;
        font-size: 20px;
        pointer-events: none;
      `}
    >
      <div
        ref={captionRef}
        className="fileboy-react-video-caption"
        css={css`
          background: rgba(0, 0, 0, 0.6);
          color: white;
          padding: 4px;
          text-align: center;
          visibility: hidden;
        `}
      />
    </div>
  )
}

interface ICaptionDefinition {
  src: string
  lang: string
  id: string
}

class VttManager {
  availableCaptions: ICaptionDefinition[] = []
  loadedCaptionData: Map<string, VTT> = new Map()
  currentCaptionId: string | null = null
  currentTime = 0
  textChangeCallbacks: ((newText: string | undefined) => void)[] = []

  // we store what text we last sent to the callback, so we can avoid calling
  //    the callbacks if the text has not changed
  lastText: string | undefined = undefined

  setAvailableCaptions(availableCaptions: ICaptionDefinition[]) {
    this.availableCaptions = availableCaptions
    this.loadedCaptionData = new Map()
  }
  setCurrentCaptionId(id: string | null) {
    this.currentCaptionId = id
    if (id) {
      this.loadCaptionData(id)
    }
    this.checkForTextChange()
  }
  setCurrentTime(time: number) {
    this.currentTime = time
    this.checkForTextChange()
  }

  onTextChange(cb: (newText: string | undefined) => void) {
    this.textChangeCallbacks = [...this.textChangeCallbacks, cb]

    return () => {
      this.textChangeCallbacks = this.textChangeCallbacks.filter(el => el !== cb)
    }
  }

  // todo implement retry algorithm
  async loadCaptionData(id: string) {
    if (this.loadedCaptionData.has(id)) {
      return
    }

    const definition = this.availableCaptions.find(definition => definition.id === id)

    if (!definition) {
      console.error(`Missing definition of caption ${id}`)
      return
    }

    try {
      const response = await fetch(definition.src)
      const vttString = await response.text()
      const vtt = VTT.parse(vttString)
      this.loadedCaptionData.set(id, vtt)
      this.checkForTextChange()
    } catch (e) {
      console.error("Error loading transcript", e)
    }
  }

  checkForTextChange() {
    const textShouldBe = this.getTextForTime(this.currentTime)

    if (this.lastText !== textShouldBe) {
      this.lastText = textShouldBe
      this.textChangeCallbacks.forEach(cb => cb(textShouldBe))
    }
  }

  getTextForTime(time: number) {
    if (!this.currentCaptionId) {
      return undefined
    }
    const captions = this.loadedCaptionData.get(this.currentCaptionId)

    if (!captions) {
      return undefined
    }

    return captions.getValueForTime(time)
  }
}

type IVttCue = {
  identifier: string
  startTime: number
  endTime: number
  payload: string
}

const TIMING_REGEX = /(?:(\d{2,}):)?(\d\d):(\d\d)\.(\d\d\d)/
const TIMING_LINE_REGEX = /^([\d:\.]+) --> ([\d:\.]+)(?: +(.*))?$/

class VTT {
  data: IVttCue[]

  constructor(data: IVttCue[]) {
    this.data = data
  }

  static parse(string: string) {
    string = string.trim().replace(/\r/g, "")

    if (!string.startsWith("WEBVTT")) {
      throw new Error("Failed to parse WEBVTT. Missing header.")
    }

    const objects = string.trim().split("\n\n")

    const cueStrings = objects.filter(
      object =>
        !object.startsWith("WEBVTT") && !object.startsWith("NOTE") && !object.startsWith("STYLE"),
    )

    const cues: IVttCue[] = []

    for (const cueString of cueStrings) {
      const lines = cueString.split("\n") //.map(line => line.trim())
      let identifier = ""
      if (!/-->/.test(lines[0])) {
        identifier = lines.shift()!.trim()
      }

      const timingLine = lines.shift()
      if (!timingLine) {
        throw new Error(`failed to parse WEBVTT. Expected timing line but got nothing`)
      }

      const timingMatch = TIMING_LINE_REGEX.exec(timingLine)
      if (!timingMatch) {
        throw new Error(`failed to parse WEBVTT. Expected timing line but got ${timingLine}`)
      }
      const [_, startTimeStr, endTimeStr, _settingsStr] = timingMatch

      const startTime = this.timeStringToSeconds(startTimeStr)
      const endTime = this.timeStringToSeconds(endTimeStr)

      const payload = lines.map(line => line.replace(/^- /, "").trim()).join("\n")

      cues.push({
        identifier,
        startTime,
        endTime,
        payload,
      })
    }

    return new VTT(cues)
  }

  static timeStringToSeconds(timeString: string) {
    const timeMatch = TIMING_REGEX.exec(timeString)

    if (!timeMatch) {
      throw new Error(`failed to parse WEBVTT. invalid time stamp string: ${timeString}`)
    }

    const [_, hoursStr, minutesStr, secondsStr, millisStr] = timeMatch

    let seconds = 0

    seconds += Number(hoursStr || 0) * 60 * 60
    seconds += Number(minutesStr || 0) * 60
    seconds += Number(secondsStr || 0)
    seconds += Number(millisStr || 0) / 1000

    return seconds
  }

  getValueForTime(time: number) {
    const line = this.data.find(line => line.startTime <= time && line.endTime > time)

    if (line) {
      return line.payload
    } else {
      return undefined
    }
  }
}
